import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import environment from "../../../environments/environment";
import { MigrationService } from "../../services/migration.service";
import {
  Company,
  MigrationCreateResponse,
  MigrationRejectResponse,
  Protocol,
  ProtocolsToMigrateResponse,
  SiteByProtocolCompany,
} from "../../interface/interfaces";
import { PaginatorFilter } from "@app/models/grids";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { MatTabChangeEvent } from "@angular/material/tabs";
import GridsConfig from "@app/common/grid.config";
import { ApiService } from "@app/services/api.service";
import { MessageService } from "@app/services/message.service";
import { ToastService } from "@app/services/toast.service";
import { MigrationStatus, PlaceHolderName } from "@app/common/constants";
import {
  RejectMigrationRequest,
  RemigrateMigrationRequest,
} from "@app/interface/models";
import { MigrationStore } from "@app/services/migration.store";

type SiteGridRow = SiteByProtocolCompany & {
  Erm_Int_Id_Display?: number | string;
};

@Component({
  selector: "app-company-migration",
  templateUrl: "./company-migration.component.html",
  styleUrls: ["./company-migration.component.scss"],
  standalone: false,
})
export class AppCompanyMigrationComponent implements OnInit {
  constructor(
    private migrationService: MigrationService,
    private apiService: ApiService,
    private messageService: MessageService,
    private toastService: ToastService,
    private migrationStore: MigrationStore,
  ) {}
  @ViewChild("pushMigration", { read: ElementRef })
  pushMIgrationBtn!: ElementRef;

  title = "Migration";
  readonly apiUrl = environment.apiUrl;
  readonly environmentName = environment.environmentName;
  // ui indicators
  loading = true;
  loadingTable = false;
  showProtocols = false;

  shouldEnableMigrationCommands = false;
  shouldEnableRejectionCommands = false;

  shouldEnableRemigrateCommands = false;
  shouldShowRemigrateMigrationCommands = false;

  cutoff_date: Date | null = null;
  // company
  company: Company | undefined;

  // Protocols table
  protocols: Protocol[] = [];
  protocolsPaginated: Protocol[] = [];
  protocolsOriginal: Protocol[] = [];
  sites: SiteGridRow[] = [];
  sitesPaginated: SiteGridRow[] = [];
  sitesOriginal: SiteGridRow[] = [];
  selectedProtocolIds: number[] = [];
  selectedSitesByProtocol: Record<string, string[]> = {};
  selectedSiteIrisIds: string[] = [];
  protocolsColumns = GridsConfig.MigrationProtocol.Columns;
  protocolsTableHeaders = GridsConfig.MigrationProtocol.Headers;
  protocolsColumnWidths = GridsConfig.MigrationProtocol.Widths;
  sitesColumns: string[] = [];
  sitesTableHeaders: string[] = [];
  sitesColumnWidths: string[] = [];
  get columnsLink(): string[] {
    const links: string[] = environment.cnx5Url ? ["title"] : [];
    if (this.isMigrationCompleted && environment.ermUrl) {
      links.push("erm_int_id");
    }
    return links;
  }
  get siteColumnsLink(): string[] {
    if (this.isMigrationCompleted && environment.ermUrl) {
      return ["Erm_Int_Id_Display"];
    }
    return [];
  }
  get filteredProtocolsColumns(): string[] {
    return this.protocolsColumns.filter((col) => col !== "erm_int_id");
  }
  get filteredProtocolsTableHeaders(): string[] {
    return this.protocolsTableHeaders.filter(
      (_h, i) => this.protocolsColumns[i] !== "erm_int_id",
    );
  }
  get filteredProtocolsColumnWidths(): string[] {
    return this.protocolsColumnWidths.filter(
      (_w, i) => this.protocolsColumns[i] !== "erm_int_id",
    );
  }
  get filteredSitesColumns(): string[] {
    return this.sitesColumns.filter((col) => col !== "Erm_Int_Id_Display");
  }
  get filteredSitesTableHeaders(): string[] {
    return this.sitesTableHeaders.filter(
      (_h, i) => this.sitesColumns[i] !== "Erm_Int_Id_Display",
    );
  }
  get filteredSitesColumnWidths(): string[] {
    return this.sitesColumnWidths.filter(
      (_w, i) => this.sitesColumns[i] !== "Erm_Int_Id_Display",
    );
  }
  readonly protocolColumnSortTypes: Record<
    string,
    "string" | "number" | "date"
  > = {
    title: "string",
    sponsor_protocol_id: "string",
    iris_protocol_id: "number",
    status: "string",
    irb_approval_dt_local: "date",
    irb_closure_dt_local: "date",
    createdOnLocal: "date",
    has_irbnet_sites: "number",
    erm_int_id: "number",
    migration_company_id: "number",
    migration_id: "number",
    migrated: "number",
  };
  readonly siteColumnSortTypes: Record<string, "string" | "number" | "date"> = {
    CX5_Site_Id: "number",
    Iris_Site_Id: "string",
    CX5_Site_Status: "string",
    Erm_Int_Id_Display: "number",
    Iris_Study_Id: "string",
    CX5_PI_Id: "number",
    CX5_PI_Email: "string",
    STATUS: "string",
    vchPartnerProjectID: "string",
    migrated: "number",
  };
  protocolSort: Sort = { active: "", direction: "" };
  siteSort: Sort = { active: "", direction: "" };
  // paginator
  paginatorFilter: PaginatorFilter = new PaginatorFilter();
  sitesPaginatorFilter: PaginatorFilter = new PaginatorFilter();
  loadingSitesTable = false;
  selectedTabIndex = 0;
  private sitesLoadedForSelectionKey: string | null = null;

  ngOnInit() {
    this.paginatorFilter.defaultPageSize = 5;
    this.sitesPaginatorFilter.defaultPageSize = 5;
    this.resetProtocolTablePagination();
    this.resetSitesTablePagination();
    this.cutoff_date = this.migrationStore.getPlaceHolderValue(
      PlaceHolderName.CUTOFF_DATE,
    )
      ? new Date(
          this.migrationStore.getPlaceHolderValue(PlaceHolderName.CUTOFF_DATE),
        )
      : null;
  }

  get isRemigrationFeatureEnabled(): boolean {
    return this.migrationStore.isPlaceHolderEnabled(
      PlaceHolderName.ENABLE_REMIGRATIONS,
    );
  }

  get isSitesRemigrationEnabled(): boolean {
    return this.migrationStore.isPlaceHolderEnabled(
      PlaceHolderName.ENABLE_SITES_REMIGRATIONS,
    );
  }

  get isMigrationCompleted(): boolean {
    return this.company?.status === MigrationStatus.migrated;
  }

  get shouldShowSiteSelectColumn(): boolean {
    return (
      this.shouldShowRemigrateMigrationCommands &&
      this.selectedProtocolIds.length > 0
    );
  }

  get remigrateButtonLabel(): string {
    const selectedProtocolCount = this.selectedProtocolIds.length;
    if (selectedProtocolCount <= 0) {
      return "Remigrate Migration";
    }

    const selectedSiteCount = this.selectedSiteIrisIds.length;
    const protocolText =
      selectedProtocolCount === 1
        ? `Remigrate ${selectedProtocolCount} Protocol`
        : `Remigrate ${selectedProtocolCount} Protocols`;

    if (selectedSiteCount <= 0) {
      return protocolText;
    }

    const siteText =
      selectedSiteCount === 1
        ? `and ${selectedSiteCount} site`
        : `and ${selectedSiteCount} sites`;

    return `${protocolText} ${siteText}`;
  }

  companySelectionChanged(event: Company | null) {
    this.company = event || undefined;
    this.resetProtocolTablePagination();
    this.resetSitesTableState();
    this.selectedTabIndex = 0;

    if (this.company?.company_id) {
      // one time setup for innitial page load
      if (!this.showProtocols) {
        this.showProtocols = true;
      }

      // set migration buttons enable status
      this.shouldEnableMigrationCommands =
        this.company?.status === null ||
        this.company.status == MigrationStatus.fixed ||
        this.company.status == MigrationStatus.reject;

      this.shouldEnableRejectionCommands = this.company?.status === null;

      this.shouldShowRemigrateMigrationCommands =
        this.company?.can_be_remigrated === true &&
        this.isRemigrationFeatureEnabled &&
        [MigrationStatus.fixed, MigrationStatus.migrated].includes(
          this.company.status,
        );

      this.shouldEnableRemigrateCommands = false;

      this.selectedProtocolIds = [];
      this.selectedSitesByProtocol = {};
      this.selectedSiteIrisIds = [];
      this.sitesLoadedForSelectionKey = null;

      this.loadStateChange(true, false, undefined, true);

      this.apiService.getCompanyProtocols(this.company.company_id).subscribe({
        next: (data: ProtocolsToMigrateResponse) => {
          this.protocols = this.migrationService.processProtocolsData(
            data.protocols,
          );

          if (this.company) {
            this.company.has_irbnet_sites = this.protocols.some(
              (element) => element.has_irbnet_sites === true,
            );
          }

          this.addRemoveIrbNetColumn(this.company?.has_irbnet_sites ?? false);

          this.protocols = this.protocols.map((p) => {
            let migrationCompanyId: number | null | undefined =
              p.migration_company_id;

            // Treat string "N/A" (or other non-numeric placeholders) as missing
            if (String(migrationCompanyId).trim().toLowerCase() === "n/a") {
              migrationCompanyId = null;
            }

            const companyId = this.company?.company_id;
            const isMigratedForSelectedCompany =
              migrationCompanyId && migrationCompanyId === companyId;

            // Allow selecting a protocol when it hasn't been migrated yet,
            // or when it was migrated for the selected company.
            const canSelect =
              !migrationCompanyId || isMigratedForSelectedCompany;

            return {
              ...p,
              migrated: isMigratedForSelectedCompany,
              canSelect: !!canSelect,
            } as Protocol;
          });

          this.addRemoveMigratedProtocolColumn(this.isMigrationCompleted);

          this.protocolsOriginal = [...this.protocols];
          this.applyProtocolSorting();

          this.shouldEnableMigrationCommands =
            this.shouldEnableMigrationCommands && this.protocols.length > 0;

          this.applyProtocolPagination();
          this.loadStateChange(false, false, undefined, false);
        },
        error: (error) => {
          this.protocols = [];
          this.protocolsOriginal = [];
          this.applyProtocolPagination();
          this.loadStateChange(false, false, undefined, false);
        },
      });
    }
    console.log(event);
  }

  addRemoveIrbNetColumn(add: boolean) {
    this.protocolsColumns = this.protocolsColumns.filter(
      (element) => element != "has_irbnet_sites",
    );
    this.protocolsTableHeaders = this.protocolsTableHeaders.filter(
      (element) => element != "IrbNet",
    );
    if (add) {
      this.protocolsColumns.push("has_irbnet_sites");
      this.protocolsTableHeaders.push("IrbNet");
    }
  }

  addRemoveMigratedProtocolColumn(add: boolean) {
    this.protocolsColumns = this.protocolsColumns.filter(
      (element) =>
        element !== "erm_int_id" &&
        element !== "migration_company_id" &&
        element !== "migration_id" &&
        element !== "migrated",
    );
    this.protocolsTableHeaders = this.protocolsTableHeaders.filter(
      (element) =>
        element !== "Erm Id" &&
        element !== "Company" &&
        element !== "Migration" &&
        element !== "Migrated",
    );
    this.protocolsColumnWidths = this.protocolsColumnWidths.filter(
      (_el, i) =>
        this.protocolsColumns[i] !== "erm_int_id" &&
        this.protocolsColumns[i] !== "migration_company_id" &&
        this.protocolsColumns[i] !== "migration_id" &&
        this.protocolsColumns[i] !== "migrated",
    );
    if (add) {
      const afterTrackingId = this.protocolsColumns.indexOf("iris_protocol_id");
      const colInsertAt =
        afterTrackingId >= 0
          ? afterTrackingId + 1
          : this.protocolsColumns.length;
      this.protocolsColumns.splice(colInsertAt, 0, "erm_int_id");
      this.protocolsTableHeaders.splice(colInsertAt, 0, "Erm Id");
      this.protocolsColumnWidths.splice(colInsertAt, 0, "40");

      this.protocolsColumns.push("migration_company_id");
      this.protocolsTableHeaders.push("Company");
      this.protocolsColumnWidths.push("50");

      this.protocolsColumns.push("migration_id");
      this.protocolsTableHeaders.push("Migration");
      this.protocolsColumnWidths.push("50");

      this.protocolsColumns.push("migrated");
      this.protocolsTableHeaders.push("Migrated");
      this.protocolsColumnWidths.push("50");
    }
  }
  protocolCellClicked($event: any) {
    if ($event.column === "title" && environment.cnx5Url) {
      const cnx5ExternalReferenceId = $event.element.cnx5ExternalReferenceId;
      window.open(
        `${environment.cnx5Url}/studies/STU-${cnx5ExternalReferenceId}/sites`,
        "_blank",
      );
    }

    if ($event.column === "erm_int_id" && environment.ermUrl) {
      const ermIntId = $event.element.erm_int_id;
      if (ermIntId) {
        this.migrationService.openStudyDetailUrl(ermIntId);
      }
    }
  }

  siteCellClicked($event: any) {
    if ($event.column === "Erm_Int_Id_Display" && environment.ermUrl) {
      const ermIntId = $event.element.Erm_Int_Id;
      if (this.hasErmIntId(ermIntId)) {
        this.migrationService.openSiteDetailUrl(ermIntId);
      }
    }
  }

  paginateProtocols(event: PageEvent) {
    this.paginatorFilter.currentPage = event.pageIndex;
    this.paginatorFilter.pageSize = event.pageSize;
    this.applyProtocolPagination();
  }

  paginateSites(event: PageEvent) {
    this.sitesPaginatorFilter.currentPage = event.pageIndex;
    this.sitesPaginatorFilter.pageSize = event.pageSize;
    this.applySitesPagination();
  }

  onProtocolSortChanged(sort: Sort) {
    this.protocolSort = sort;
    this.paginatorFilter.currentPage = 0;
    this.applyProtocolSorting();
  }

  onSiteSortChanged(sort: Sort) {
    this.siteSort = sort;
    this.sitesPaginatorFilter.currentPage = 0;
    this.applySitesSorting();
  }

  onTabChange(event: MatTabChangeEvent) {
    this.selectedTabIndex = event.index;
    if (event.index === 1) {
      this.loadSitesForSelectedProtocols();
    }
  }

  resetProtocolTablePagination() {
    this.paginatorFilter.currentPage = 0;
    this.paginatorFilter.pageSize = this.paginatorFilter.defaultPageSize || 5;
  }

  resetSitesTablePagination() {
    this.sitesPaginatorFilter.currentPage = 0;
    this.sitesPaginatorFilter.pageSize =
      this.sitesPaginatorFilter.defaultPageSize || 5;
  }

  // ************* Publish migration *************
  publishMigrationClicked() {
    if (!this.company?.company_id) return;

    if (this.company.status === MigrationStatus.reject) {
      this.migrationService.publishMigrationModal({
        company_name: this.company?.company_name,
        onConfirm: () => this.publishMigration(),
        onCancel: () => console.log("User cancelled rejected migration"),
        title: "Queue Rejected Migration",
        message:
          "This migration has been rejected. Are you sure you want to queue this migration for processing?",
        confirmText: "Queue",
      });
      return;
    }
    this.migrationService.publishMigrationModal({
      company_name: this.company?.company_name,
      onConfirm: () => this.publishMigration(),
      onCancel: () => console.log("User cancelled publish migration"),
    });
  }

  private publishMigration() {
    if (this.company?.company_id) {
      this.loading = true;
      this.apiService.pushCompany(this.company?.company_id).subscribe({
        next: (data: MigrationCreateResponse) => {
          this.shouldEnableMigrationCommands = false;
          this.loadStateChange(
            false,
            true,
            `Migration queued with id ${data.id}`,
          );
        },
        error: (error) => {
          this.loading = false;
        },
      });
    }
  }

  // ************* Reject migration *************
  rejectMigrationClicked() {
    this.migrationService.rejectShowMigrationModal(
      this.company?.company_name,
      (comments) => this.rejectMigration(comments),
      () => console.log("User cancelled reject migration"),
    );
  }

  private rejectMigration(comments?: string) {
    if (!this.company?.company_id) return;

    if (!comments || comments.trim() === "") {
      this.toastService.error("Comments are required for rejection");
      return;
    }

    this.loadStateChange(true);
    this.shouldEnableMigrationCommands = false;

    this.apiService
      .rejectCompanyMigration(
        this.company?.company_id,
        new RejectMigrationRequest(comments),
      )
      .subscribe({
        next: (data: MigrationRejectResponse) => {
          this.loadStateChange(false, true, `${data.message}`);
        },
        error: (error) => {
          this.loadStateChange(false);
        },
      });
  }

  // ************* Remigrate migration *************
  protocolSelectionChanged(selectedRows: Protocol[]) {
    this.selectedProtocolIds = selectedRows
      .map((p) => p.iris_protocol_id)
      .filter((id): id is number => id != null);

    this.selectedSitesByProtocol = {};
    this.selectedSiteIrisIds = [];

    this.sitesLoadedForSelectionKey = null;

    this.shouldEnableRemigrateCommands =
      this.shouldShowRemigrateMigrationCommands &&
      this.selectedProtocolIds.length > 0;

    // Keep the sites select column in sync when sites are already loaded
    if (this.sitesColumns.length > 0) {
      this.updateSiteSelectColumn();
    }

    if (this.selectedTabIndex === 1) {
      this.loadSitesForSelectedProtocols();
    }
  }

  siteSelectionChanged(selectedRows: SiteGridRow[]) {
    const groupedSitesByProtocol: Record<string, string[]> = {};

    selectedRows.forEach((site) => {
      const protocolKey = (site.Iris_Study_Id ?? "").toString().trim();
      const irisSiteId = (site.Iris_Site_Id ?? "").toString().trim();

      if (!protocolKey || !irisSiteId) {
        return;
      }

      if (!groupedSitesByProtocol[protocolKey]) {
        groupedSitesByProtocol[protocolKey] = [];
      }

      if (!groupedSitesByProtocol[protocolKey].includes(irisSiteId)) {
        groupedSitesByProtocol[protocolKey].push(irisSiteId);
      }
    });

    this.selectedSitesByProtocol = groupedSitesByProtocol;

    this.selectedSiteIrisIds = selectedRows
      .map((site) => site.Iris_Site_Id)
      .filter((id): id is string => !!id && id.trim().length > 0);
  }

  remigrateMigrationClicked() {
    if (!this.isRemigrationFeatureEnabled) {
      this.toastService.info(
        "Remigration is currently disabled by system settings.",
      );
      return;
    }

    // first check if the selected protocols are not already migrated
    this.apiService
      .filterNotMigratedProtocols(
        this.company?.migration_id ?? 0,
        this.selectedProtocolIds,
      )
      .subscribe({
        next: (non_migrated_protocols: number[]) => {
          const protocols_to_remigrate = this.selectedProtocolIds.filter((id) =>
            non_migrated_protocols.includes(id),
          );
          const migrated_by_other_companies = this.selectedProtocolIds.filter(
            (id) => !protocols_to_remigrate.includes(id),
          );

          if (protocols_to_remigrate.length === 0) {
            this.toastService.info(
              "All selected protocols have already been migrated by another company. Please select other protocols to remigrate.",
            );
            return;
          }

          // select only computed protocols to remigrate
          this.selectedProtocolIds = protocols_to_remigrate;
          this.filterSelectedSitesToProtocols(protocols_to_remigrate);

          // Enforce site coverage only when the user started selecting sites.
          let missingProtocolIds: number[] = [];
          if (this.selectedSiteIrisIds.length > 0) {
            missingProtocolIds = protocols_to_remigrate.filter(
              (protocolId) =>
                !(this.selectedSitesByProtocol[String(protocolId)] ?? [])
                  .length,
            );
          }

          this.migrationService.remigrateShowMigrationModal(
            this.company?.company_name,
            (comments) => this.remigrateMigration(comments),
            () => console.log("User cancelled remigrate migration"),
            migrated_by_other_companies,
            protocols_to_remigrate,
            this.selectedSiteIrisIds,
            missingProtocolIds,
          );
        },
        error: (error) => {
          this.toastService.error("Failed to filter protocols");
          console.error(error);
        },
      });
  }

  private remigrateMigration(comments?: string) {
    if (!this.company?.migration_id) return;

    if (!comments || comments.trim() === "") {
      this.toastService.error("Comments are required for remigration");
      return;
    }

    this.loadStateChange(true);
    this.shouldEnableMigrationCommands = false;

    this.apiService
      .remigrateCompanyMigration(
        this.company.migration_id,
        new RemigrateMigrationRequest(
          comments,
          this.company.migration_id,
          this.selectedProtocolIds,
          this.selectedSitesByProtocol,
        ),
      )
      .subscribe({
        next: (data: MigrationCreateResponse) => {
          this.loadStateChange(
            false,
            true,
            `Remigration queued with id ${data.id}`,
          );
        },
        error: (error) => {
          this.loadStateChange(false);
        },
      });
  }

  // ******** PRIVATE ********
  private loadStateChange(
    loading: boolean,
    sendReloadEvent: boolean = false,
    messageText?: string,
    loadingTable: boolean = false,
  ) {
    this.loading = loading;
    this.migrationStore.setLoading(loading);
    this.loadingTable = loadingTable;

    if (sendReloadEvent) {
      this.messageService.sendReloadMessage(true);
    }
    if (messageText) {
      this.toastService.success(messageText);
    }
  }

  private applyProtocolPagination() {
    const skipRecords: number =
      this.paginatorFilter.currentPage * this.paginatorFilter.pageSize;
    this.protocolsPaginated = this.protocols
      .slice(skipRecords)
      .slice(0, this.paginatorFilter.pageSize);
    this.paginatorFilter.count = this.protocols.length;
  }

  private applyProtocolSorting() {
    this.protocols = this.sortRows(
      this.protocolsOriginal,
      this.protocolSort,
      this.protocolColumnSortTypes,
    ) as Protocol[];
    this.applyProtocolPagination();
  }

  private applySitesPagination() {
    const skipRecords: number =
      this.sitesPaginatorFilter.currentPage *
      this.sitesPaginatorFilter.pageSize;
    this.sitesPaginated = this.sites
      .slice(skipRecords)
      .slice(0, this.sitesPaginatorFilter.pageSize);
    this.sitesPaginatorFilter.count = this.sites.length;
  }

  private applySitesSorting() {
    this.sites = this.sortRows(
      this.sitesOriginal,
      this.siteSort,
      this.siteColumnSortTypes,
    ) as SiteByProtocolCompany[];
    this.applySitesPagination();
  }

  private resetSitesTableState() {
    this.sites = [];
    this.sitesPaginated = [];
    this.sitesOriginal = [];
    this.selectedSitesByProtocol = {};
    this.selectedSiteIrisIds = [];
    this.sitesColumns = [];
    this.sitesTableHeaders = [];
    this.sitesColumnWidths = [];
    this.resetSitesTablePagination();
    this.loadingSitesTable = false;
    this.sitesLoadedForSelectionKey = null;
  }

  private loadSitesForSelectedProtocols() {
    if (!this.company?.company_id) {
      return;
    }

    const protocolIdsToLoad =
      this.selectedProtocolIds.length > 0
        ? this.selectedProtocolIds
        : this.protocols
            .map((p) => p.iris_protocol_id)
            .filter((id): id is number => id != null);

    if (protocolIdsToLoad.length === 0) {
      this.resetSitesTableState();
      return;
    }

    const sortedProtocolIds = Array.from(new Set(protocolIdsToLoad)).sort(
      (a, b) => a - b,
    );
    const scope = this.selectedProtocolIds.length > 0 ? "selected" : "all";
    const selectionKey = `${this.company.company_id}:${scope}:${sortedProtocolIds.join(",")}`;

    if (this.sitesLoadedForSelectionKey === selectionKey) {
      return;
    }

    this.loadingSitesTable = true;
    this.apiService
      .getSitesByProtocolsAndCompany(this.company.company_id, sortedProtocolIds)
      .subscribe({
        next: (data: SiteByProtocolCompany[]) => {
          this.sites = (data || []).map((site) => {
            const hasErmIntId = this.hasErmIntId(site.Erm_Int_Id);
            const isMigratedForSelectedCompany =
              (site as SiteByProtocolCompany & { company_id?: number })
                .company_id === this.company?.company_id;
            const canSelect =
              site.migration_id === null || isMigratedForSelectedCompany;
            return {
              ...site,
              Erm_Int_Id_Display: hasErmIntId ? site.Erm_Int_Id : "N/A",
              migrated: isMigratedForSelectedCompany,
              canSelect: canSelect,
            };
          });
          this.sitesOriginal = [...this.sites];
          this.configureSitesGrid();
          this.resetSitesTablePagination();
          this.applySitesSorting();
          this.sitesLoadedForSelectionKey = selectionKey;
          this.loadingSitesTable = false;
        },
        error: () => {
          this.sites = [];
          this.sitesOriginal = [];
          this.configureSitesGrid();
          this.applySitesPagination();
          this.loadingSitesTable = false;
          this.toastService.error("Failed to retrieve sites");
        },
      });
  }

  private configureSitesGrid() {
    if (!this.sites.length) {
      this.sitesColumns = [];
      this.sitesTableHeaders = [];
      this.sitesColumnWidths = [];
      return;
    }

    const filteredColumns = Object.keys(this.sites[0]).filter(
      (column) =>
        column !== "rn" &&
        column !== "canSelect" &&
        column !== "Erm_Int_Id" &&
        (column !== "migration_id" || this.isMigrationCompleted) &&
        (column !== "company_id" || this.isMigrationCompleted) &&
        (column !== "migrated" || this.isMigrationCompleted) &&
        (column !== "Erm_Int_Id_Display" || this.isMigrationCompleted),
    );
    const hasErmIntIdDisplay = filteredColumns.includes("Erm_Int_Id_Display");
    const baseColumns = filteredColumns.includes("Iris_Study_Id")
      ? [
          "Iris_Study_Id",
          ...filteredColumns.filter(
            (column) =>
              column !== "Iris_Study_Id" && column !== "Erm_Int_Id_Display",
          ),
        ]
      : filteredColumns.filter((column) => column !== "Erm_Int_Id_Display");

    if (hasErmIntIdDisplay) {
      const irisStudyIdIndex = baseColumns.indexOf("Iris_Study_Id");
      const insertAt = irisStudyIdIndex >= 0 ? irisStudyIdIndex + 1 : 0;
      baseColumns.splice(insertAt, 0, "Erm_Int_Id_Display");
    }

    this.sitesColumns = this.shouldShowSiteSelectColumn
      ? ["select", ...baseColumns]
      : baseColumns;

    this.sitesTableHeaders = this.sitesColumns.map((column) => {
      if (column === "vchPartnerProjectID") {
        return "Project ID";
      }

      if (column === "Erm_Int_Id_Display") {
        return "Erm Id";
      }

      if (column === "migrated") {
        return "Migrated";
      }

      return column
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    });
    this.sitesColumnWidths = this.sitesColumns.map((col) =>
      col === "select" ? "30" : "*",
    );
  }

  private updateSiteSelectColumn(): void {
    const hasSelectCol = this.sitesColumns[0] === "select";
    if (this.shouldShowSiteSelectColumn && !hasSelectCol) {
      this.sitesColumns = ["select", ...this.sitesColumns];
      this.sitesTableHeaders = ["Select", ...this.sitesTableHeaders];
      this.sitesColumnWidths = ["30", ...this.sitesColumnWidths];
    } else if (!this.shouldShowSiteSelectColumn && hasSelectCol) {
      this.sitesColumns = this.sitesColumns.slice(1);
      this.sitesTableHeaders = this.sitesTableHeaders.slice(1);
      this.sitesColumnWidths = this.sitesColumnWidths.slice(1);
    }
  }

  private filterSelectedSitesToProtocols(protocolIds: number[]): void {
    const allowedProtocolKeys = new Set(protocolIds.map((id) => String(id)));

    const filteredSitesByProtocol: Record<string, string[]> = {};
    Object.entries(this.selectedSitesByProtocol).forEach(
      ([protocolKey, siteIds]) => {
        if (allowedProtocolKeys.has(protocolKey)) {
          filteredSitesByProtocol[protocolKey] = siteIds;
        }
      },
    );

    this.selectedSitesByProtocol = filteredSitesByProtocol;
    this.selectedSiteIrisIds = Object.values(filteredSitesByProtocol).flat();
  }

  private sortRows(
    rows: unknown[],
    sort: Sort,
    columnTypes: Record<string, "string" | "number" | "date">,
  ): unknown[] {
    const sortedRows = [...rows];

    if (!sort.active || !sort.direction) {
      return sortedRows;
    }

    const direction = sort.direction === "asc" ? 1 : -1;
    const columnType = columnTypes[sort.active] || "string";

    sortedRows.sort((left: any, right: any) => {
      const leftValue = this.normalizeSortValue(
        left?.[sort.active],
        columnType,
      );
      const rightValue = this.normalizeSortValue(
        right?.[sort.active],
        columnType,
      );

      if (leftValue < rightValue) {
        return -1 * direction;
      }

      if (leftValue > rightValue) {
        return 1 * direction;
      }

      return 0;
    });

    return sortedRows;
  }

  private normalizeSortValue(
    value: unknown,
    columnType: "string" | "number" | "date",
  ): string | number {
    if (columnType === "number") {
      if (typeof value === "boolean") {
        return value ? 1 : 0;
      }

      if (typeof value === "number") {
        return value;
      }

      if (value === null || value === undefined || value === "") {
        return -1;
      }

      const parsed = Number(value);
      return Number.isNaN(parsed) ? -1 : parsed;
    }

    if (columnType === "date") {
      if (!value) {
        return -1;
      }

      const timestamp = new Date(String(value)).getTime();
      return Number.isNaN(timestamp) ? -1 : timestamp;
    }

    return String(value ?? "").toLowerCase();
  }

  private hasErmIntId(value: unknown): boolean {
    if (value == null) {
      return false;
    }

    const normalized = String(value).trim().toLowerCase();
    return (
      normalized !== "" &&
      normalized !== "null" &&
      normalized !== "n/a" &&
      normalized !== "na"
    );
  }
}
